import ReactMarkdown from 'react-markdown';
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "./ui/carousel"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "./ui/sheet"

import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "./ui/resizable";
import { SessionDetails, User } from "../lib/types";
import { ReloadIcon } from "@radix-ui/react-icons";
import React, { FC, ReactNode, useCallback, useState } from "react";
import { ScrollArea } from "./ui/scroll-area"
import { getSessionDetails } from '../lib/api';


export default function SessionDetailsComponent({sessionDetails, refreshSessionsDetails, selectedUser}: {sessionDetails: SessionDetails | null, refreshSessionsDetails: () => void, selectedUser: User | null}) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const TextRenderer: FC<{ children: ReactNode; node?: any }> = ({ children, node }) => {
    const processText = (text: string) => {
        const regex = /\[(\d+)\]/g;
        const parts = text.split(regex);
        return parts.map((part, index) => {
            if (index % 2 === 1) {
                const citationNumber = parseInt(part, 10);
                return (
                    <Button
                        className='bg-gray-100 dark:bg-gray-800 text-xs'
                        variant={'outline'}
                        key={`citation-${index}`}
                        size={'sm'}
                        // onClick={() => handleCitationClick(citationNumber)}
                        >
                        {citationNumber}
                    </Button>
                );
            }
            return part;
        });
    };

    if (typeof children === 'string') {
        if (node?.tagName === 'li') {
            // console.log("Found LI item")
            return <li>{processText(children)}</li>;
        } else if(node?.tagName === 'p') {
            // console.log("Found P item")
            return <p>{processText(children)}</p>;
        }
        return <>{processText(children)}</>;
    }

    if (Array.isArray(children)) {
        if(node?.tagName === 'li') {
            return (
                <li>
                    {children.map((child, index) => 
                        typeof child === 'string' ? 
                            <React.Fragment key={index}>{processText(child)}</React.Fragment> : 
                            <React.Fragment key={index}>{child}</React.Fragment>
                    )}
                </li>
            );
        } else if (node?.tagName === 'p') {
            return (
                <p>
                    {children.map((child, index) => 
                        typeof child === 'string' ? 
                            <React.Fragment key={index}>{processText(child)}</React.Fragment> : 
                            <React.Fragment key={index}>{child}</React.Fragment>
                    )}
                </p>
            );
        }
        return (
            <>
                {children.map((child, index) => 
                    typeof child === 'string' ? 
                        <React.Fragment key={index}>{processText(child)}</React.Fragment> : 
                        <React.Fragment key={index}>{child}</React.Fragment>
                )}
            </>
        );
    }

    if (node?.tagName === 'li') {
        // console.log("Found LI item")
        return <li>{children}</li>;
    }

    return <>{children}</>;
};
  const [sessionId, setSessionId] = useState<string | null>(null);  
  const [sessionDetail, setSessionDetail] = useState<SessionDetails | null>(sessionDetails);  

  const handleSubmit = useCallback((e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if(sessionId){
      getSessionDetails(sessionId).then((data ) => {
          setSessionDetail(data as SessionDetails);
      });
    }
  }, [sessionId]);
  
  return (
    <Card id="ssd-card" className=' '>
      <CardHeader className='p-2'>
        <CardTitle>
          <div className="flex flex-row p-2 items-center">
            <span>Session Details: </span>
            {(!selectedUser || !selectedUser.user_id ) && 
            <div>
              <form onSubmit={handleSubmit}>
              <input type="text" placeholder="Session id" value={sessionId ?? ''} onChange={(e) => setSessionId(e.target.value)} className="border border-gray-300 rounded-md p-2" />
              </form>
            </div>}
            <Button onClick={() => refreshSessionsDetails()} size={'icon'} variant={'outline'} className="ml-2 border-0">
              <ReloadIcon className="w-4 h-3 stroke-4" />
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="w-full pl-10 pr-10">
        
          <Carousel >
            <CarouselContent >
              {sessionDetail && sessionDetail.queries?.map((query, index) => (
                <CarouselItem key={index} className='h-screen'>
                  <ResizablePanelGroup
                      direction="horizontal"
                      className="rounded-lg border"
                    >
                      <ResizablePanel defaultSize={55} className='p-2'>
                        <ScrollArea className='h-5/6'>
                          <span className="font-semibold">Question: {query.query_text}</span>
                          {
                          sessionDetail.answers?.length > index && sessionDetail.answers[index].query_id === query.query_id && 
                          <ReactMarkdown 
                          // className="text-gray-500 mb-4 text-justify text-sm tracking-wide font-inter font-normal leading-6"
                              components={{
                              p: ({ node, children }) => <TextRenderer node={node}>{children}</TextRenderer>,
                              li: ({ node, children }) => <TextRenderer node={node}>{children}</TextRenderer>,
                            }}
                            >
                          {sessionDetail.answers[index].answer_text}
                          </ReactMarkdown>
                          }
                        </ScrollArea>
                      </ResizablePanel>
                      <ResizableHandle />
                      <ResizablePanel defaultSize={45}>
                        <ResizablePanelGroup direction="vertical">
                          <ResizablePanel defaultSize={15} className="p-2">
                            <ScrollArea className='h-full'>
                              <div className="font-semibold sticky top-0">Suggested Questions:</div>
                              {sessionDetail.suggestedQuestions.filter((suggestedQuestion) => suggestedQuestion.query_id === query.query_id).map((suggestedQuestion, index) => (
                                <div key={index}>
                                  <span className='text-sm'>{index+1}. {suggestedQuestion.question_text}</span>
                                </div>
                              ))}
                            </ScrollArea>
                          </ResizablePanel>
                          <ResizableHandle />
                          <ResizablePanel defaultSize={20}>
                            <ScrollArea className='h-full p-2'>
                              <div className="font-semibold sticky top-0">Cited Search Results:</div>
                              {sessionDetail.answers.filter((answer) => answer.query_id === query.query_id).map((answer, index) => (
                                <div key={index}>
                                  {answer.cited_results.map((cited_result, index) => (
                                    <div key={index}>
                                      <Sheet key={index}>
                                        <SheetTrigger><span className='truncate text-sm' title={cited_result.snippet}>{index+1}. {cited_result.snippet}</span></SheetTrigger>
                                        <SheetContent className="w-[200px] sm:w-[540px]">
                                          <ScrollArea className='h-full p-0 overflow-y-auto'>
                                            <SheetHeader>
                                              <SheetTitle>Cited Search Result</SheetTitle>
                                              <div>
                                                <span className='font-semibold'>Title: <span className='text-sm text-gray-500'>{cited_result.title}</span></span>
                                              </div>
                                              <div>
                                                <span className='font-semibold'>Source Date: <span className='text-sm text-gray-500'>{cited_result.source_date}</span></span>
                                              </div>
                                              <div>
                                                <span className='font-semibold'>Created At: <span className='text-sm text-gray-500'>{ cited_result.created_at ? new Date(cited_result.created_at + 'Z').toLocaleString(navigator.language) : ""}</span></span>
                                              </div>
                                              <div>
                                                <span className='font-semibold'>Updated At: <span className='text-sm text-gray-500'>{cited_result.updated_at ? new Date(cited_result.updated_at + 'Z').toLocaleString(navigator.language) : ""}</span></span>
                                              </div>
                                              <div>
                                                <span className='font-semibold'>Source Type: <span className='text-sm text-gray-500'>{cited_result.source_type}</span></span>
                                              </div>
                                              <div>
                                                <span className='font-semibold'>Source Domain: <span className='text-sm text-gray-500'>{cited_result.source_domain}</span></span>
                                              </div>
                                              <div>
                                                  <span className='font-semibold'>URL: <span className='text-sm text-gray-500'><a target="_blank" rel="noopener noreferrer" href={cited_result.url}>{cited_result.url}</a></span></span>
                                              </div>
                                              <div>
                                                <span className='font-semibold'>query_id: <span className='text-sm text-gray-500'>{cited_result.query_id}</span></span>
                                              </div>
                                              <div>
                                                <span className='font-semibold'>result_id: <span className='text-sm text-gray-500'>{cited_result.result_id}</span></span>
                                              </div>
                                              <div>
                                                <span className='font-semibold'>Snippet: </span>
                                                <div className='text-sm text-gray-500'>{cited_result.snippet}</div>
                                              </div>
                                              <ScrollArea className='h-48 p-0 overflow-y-auto'>
                                                <span className='font-semibold sticky top-0'>Full Text: </span>
                                                <div className='text-sm text-gray-500'>{cited_result.full_text}</div>
                                              </ScrollArea>
                                              <ScrollArea className='h-22 pt-1 overflow-y-auto'>
                                                <span className='font-semibold sticky top-0'>Embedding vector: </span>
                                                <div className='text-sm text-gray-500'>{cited_result.embedding_vector}</div>
                                              </ScrollArea>
                                            </SheetHeader>
                                          </ScrollArea>
                                        </SheetContent>
                                      </Sheet>
                                    </div>
                                  ))}
                                </div>
                              ))}
                            </ScrollArea>
                          </ResizablePanel> 
                          <ResizableHandle />
                          <ResizablePanel >
                            <ScrollArea className='h-full p-2'>
                              <div className="font-semibold sticky top-0">Search Results:</div>
                              {sessionDetail.searchResults.filter((searchResult) => searchResult.query_id === query.query_id).map((searchResult, index) => (
                                <Sheet key={index}>
                                  <SheetTrigger><span className='truncate text-sm' title={searchResult.snippet}>{index+1}. {searchResult.snippet}</span></SheetTrigger>
                                  <SheetContent className="w-[200px] sm:w-[540px]">
                                    <ScrollArea className='h-full p-0 overflow-y-auto'>
                                      <SheetHeader>
                                          <SheetTitle>Search Result</SheetTitle>
                                        <div>
                                          <span className='font-semibold'>Title: <span className='text-sm text-gray-500'>{searchResult.title}</span></span>
                                        </div>
                                        <div>
                                          <span className='font-semibold'>Source Date: <span className='text-sm text-gray-500'>{searchResult.source_date}</span></span>
                                        </div>
                                        <div>
                                          <span className='font-semibold'>Created At: <span className='text-sm text-gray-500'>{searchResult.created_at ? new Date(searchResult.created_at + 'Z').toLocaleString(navigator.language) : ""}</span></span>
                                        </div>
                                        <div>
                                          <span className='font-semibold'>Updated At: <span className='text-sm text-gray-500'>{searchResult.updated_at ? new Date(searchResult.updated_at + 'Z').toLocaleString(navigator.language) : ""}</span></span>
                                        </div>
                                        <div>
                                          <span className='font-semibold'>Source Type: <span className='text-sm text-gray-500'>{searchResult.source_type}</span></span>
                                        </div>
                                        <div>
                                          <span className='font-semibold'>Source Domain: <span className='text-sm text-gray-500'>{searchResult.source_domain}</span></span>
                                        </div>
                                        <div>
                                          <span className='font-semibold'>URL: <span className='text-sm text-gray-500'><a target="_blank" rel="noopener noreferrer"  href={searchResult.url}>{searchResult.url}</a></span></span>
                                        </div>
                                        <div>
                                          <span className='font-semibold'>query_id: <span className='text-sm text-gray-500'>{searchResult.query_id}</span></span>
                                        </div>
                                        <div>
                                          <span className='font-semibold'>result_id: <span className='text-sm text-gray-500'>{searchResult.result_id}</span></span>
                                        </div>
                                        <div>
                                          <span className='font-semibold'>Snippet: </span>
                                          <div className='text-sm text-gray-500'>{searchResult.snippet}</div>
                                        </div>
                                        <ScrollArea className='h-48 p-0 overflow-y-auto'>
                                          <span className='font-semibold sticky top-0'>Full Text: </span>
                                          <div className='text-sm text-gray-500'>{searchResult.full_text}</div>
                                        </ScrollArea>
                                        <ScrollArea className='h-22 pt-1 overflow-y-auto'>
                                          <span className='font-semibold sticky top-0'>Embedding vector: </span>
                                          <div className='text-sm text-gray-500'>{searchResult.embedding_vector}</div>
                                        </ScrollArea>
                                      </SheetHeader>
                                    </ScrollArea>
                                  </SheetContent>
                                </Sheet>
                              ))}
                            </ScrollArea>
                          </ResizablePanel>
                        </ResizablePanelGroup>
                      </ResizablePanel>
                    </ResizablePanelGroup>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious />
            <CarouselNext />
          </Carousel>
        </div>
      </CardContent>
    </Card>
  );
}